import { inject } from '@angular/core';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStoreFeature, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { pipe, switchMap, tap } from 'rxjs';
import { EntitlementsDataService } from 'src/shared/services/entitlements.data.service';
import { entities } from 'src/shared/services/client/client';

export type UserEntitlementsState = 'initial' | 'loading' | 'complete' | 'error';

export type UserEntitlementsFeatureState = {
  userEntitlementsState: UserEntitlementsState;
  userEntitlementsError: string;
  userEntitlements: entities.EntitlementCalculatorResponse | null;
};

const initialState: UserEntitlementsFeatureState = {
  userEntitlementsState: 'initial',
  userEntitlementsError: '',
  userEntitlements: null,
};

/**
 * Signal store feature for user entitlements. Provides getUserEntitlements rxMethod
 * and state: userEntitlementsState, userEntitlementsError, userEntitlements.
 */
export function withUserEntitlements() {
  return signalStoreFeature(
    withState(initialState),
    withMethods((store, entitlementsService = inject(EntitlementsDataService)) => ({
      getUserEntitlements: rxMethod<void>(
        pipe(
          tap(() =>
            patchState(store, {
              userEntitlementsState: 'loading' as UserEntitlementsState,
              userEntitlementsError: '',
            })
          ),
          switchMap(() => entitlementsService.getUserEntitlements()),
          tapResponse({
            next: (userEntitlements) =>
              patchState(store, {
                userEntitlements,
                userEntitlementsState: 'complete' as UserEntitlementsState,
              }),
            error: (error: unknown) =>
              patchState(store, {
                userEntitlementsState: 'error' as UserEntitlementsState,
                userEntitlementsError: error instanceof Error ? error.message : String(error),
              }),
          })
        )
      ),
    }))
  );
}
